import { FC } from 'react';
import { Routes, Route, Navigate, BrowserRouter } from 'react-router-dom';
import { PrivateRoutes } from './PrivateRoutes';
import { ErrorsPage } from '../modules/errors/ErrorsPage';
import { Logout, AuthPage } from '../modules/auth';
import { App } from '../App';
import { useSelector } from 'react-redux';
import { getAuthTokenSelector, getIsLoggedIn } from '../modules/auth/core/selectors';
import { useQuery } from '@shared/index';

const { PUBLIC_URL } = process.env;

const AppRoutes: FC = () => {
  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <MainRoutes />
    </BrowserRouter>
  );
};

const MainRoutes: FC = () => {
  const currentUser: boolean = useSelector(getIsLoggedIn);
  const accessToken = useSelector(getAuthTokenSelector);
  const { getQueryParam } = useQuery();

  const urlSessionToken = getQueryParam('sessionToken');
  const prevLocalSessionToken = localStorage.getItem('auth.sessionToken');

  // Persist URL sessionToken to localStorage immediately — before any routing
  // decisions — so it's always available for the refresh fallback in sagas
  if (urlSessionToken) {
    localStorage.setItem('auth.sessionToken', urlSessionToken);
    localStorage.setItem('auth.sessionTokenTimestamp', String(Date.now()));
  }

  const shouldRelogin = Boolean(
    urlSessionToken &&
    prevLocalSessionToken &&
    urlSessionToken !== prevLocalSessionToken &&
    accessToken,
  );

  // When a magic link arrives while the user has stale auth (redux-persist) but
  // no stored sessionToken, force through the auth flow instead of silently
  // landing on PrivateRoutes with an expired access token
  const shouldForceAuth = Boolean(urlSessionToken && !prevLocalSessionToken && currentUser);

  return (
    <Routes>
      <Route element={<App />}>
        <Route path="error/*" element={<ErrorsPage />} />
        <Route path="logout" element={<Logout />} />
        {currentUser && !shouldRelogin && !shouldForceAuth ? (
          <>
            <Route path="/*" element={<PrivateRoutes />} />
            <Route index element={<Navigate to="/dashboard" />} />
          </>
        ) : (
          <>
            <Route path="auth/*" element={<AuthPage shouldRelogin={shouldRelogin} />} />
            <Route path="*" element={<Navigate to="/auth" />} />
          </>
        )}
      </Route>
    </Routes>
  );
};

export { AppRoutes };
